// src/pages/customer/TeamManagement.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import apiService from "../../services/api";
import { Select } from "../../arc/components/select/select";
import { Dialog, DialogContent } from "../../arc/components/dialog/dialog";
import { Input } from "../../arc/components/input/input";
import { PasswordField } from "../../arc/components/password-field/password-field";
import { PasswordStrength } from "../../arc/components/password-strength/password-strength";
import { useToastStack } from "../../arc/components/toast-stack/toast-stack";
import { SortableDataTable } from "../../arc/components/sortable-data-table/sortable-data-table";
import { Skeleton } from "../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../utils/themeTransition";
import { AXO_PASSWORD_RULES } from "../../config/passwordRules";
import {
  CAPABILITIES,
  getRoleDefinition,
  normaliseRole,
  roleLabel,
  assignableRoles,
  canManageUser,
  hasCapability,
} from "../../config/roles";
import {
  AccountSettingsModal,
  ACCOUNT_SETTINGS_STYLES,
} from "./CustomerDashboard";

const styles = `
  /* Cal.com-style design system, mirrors CustomerDashboard.
   * White canvas, monochrome-flat cards, hairline borders, no gradients,
   * accent locked to AXO navy.
   */

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }



  /* Settings dropdown styling lives in the base rules below and adapts to
     dark mode via the shared design tokens. */
  .dash-root.dark tbody tr:hover { background: var(--row-hover); }
  .dash-root.dark tbody tr:hover td { background: var(--row-hover) !important; }
  .dash-root.dark thead tr { background: var(--surface-sunken); }
  .dash-root.dark .table-toolbar { background: var(--surface-raised); }
  .dash-root.dark .skeleton { background: linear-gradient(90deg, var(--surface-raised) 25%, var(--border) 50%, var(--surface-raised) 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
  .dash-root.dark .field-input { background: var(--bg); }
  .dash-root.dark .toggle-track { background: var(--border-strong); }
  .dash-root.dark .pagination { background: var(--surface-sunken); }

  html, body { overflow-x: hidden; }
  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: var(--text-base); line-height: 1.5; -webkit-font-smoothing: antialiased; overflow-x: clip; overflow-wrap: anywhere; }

  /* ── HEADER, flat AXO navy, matches CustomerDashboard ─────────────── */
  .dash-header { background: var(--header-bg); border-bottom: 1px solid var(--header-border); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1580px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .dash-brand-logo { width: 54px; height: 54px; border-radius: var(--radius); overflow: hidden; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.13); padding: 4px; }
  .dash-brand-logo img { width: 100%; height: 100%; object-fit: contain; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 2px; }
  .dash-brand-name { font-size: 14px; font-weight: 600; letter-spacing: -0.01em; color: #ffffff; }
  .dash-brand-sub { font-family: var(--sans); font-size: 13px; font-weight: 500; letter-spacing: 0; color: var(--header-text-muted); }
  .dash-nav { display: flex; align-items: center; gap: 6px; margin-left: auto; }

  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); border-radius: var(--radius-sm); color: var(--header-text); transition: background var(--transition), border-color var(--transition), color var(--transition); text-decoration: none; }
  .btn-back:hover { background: var(--header-btn-hover-bg); border-color: rgba(255,255,255,0.22); color: #ffffff; }

  .dash-header .btn-ghost { background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); color: var(--header-text); }
  .dash-header .btn-ghost:hover { background: var(--header-btn-hover-bg); border-color: rgba(255,255,255,0.22); color: #ffffff; }
  .dash-header .user-pill { background: var(--header-pill-bg); border: 1px solid var(--header-btn-border); color: var(--header-text); }
  .dash-header .online-dot { background: var(--green); box-shadow: 0 0 5px rgba(16,185,129,0.5); }
  /* Settings dropdown is a Cal.com surface card (white canvas + hairline
     border), even though it opens from the navy header. */

  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; cursor: pointer; white-space: nowrap; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; transition: color var(--transition), background var(--transition), border-color var(--transition), box-shadow var(--transition), opacity var(--transition); }
  .btn:focus-visible { box-shadow: 0 0 0 3px var(--accent-dim); }
  .btn:disabled { opacity: 0.52; cursor: not-allowed; }
  .btn-sm { min-height: 32px; padding: 0 12px; }
  .btn-md { min-height: 36px; padding: 0 16px; }
  .btn-lg { min-height: 40px; padding: 0 20px; }
  .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-ghost:hover { background: var(--surface-raised); border-color: var(--border-strong); color: var(--text); }
  .btn-primary { background: var(--accent); color: #fff; border: 1px solid transparent; letter-spacing: 0.01em; }
  .btn-primary:hover { background: var(--accent-hover); }
  .btn-primary:disabled { background: var(--text-faint); color: var(--surface); border-color: transparent; cursor: not-allowed; opacity: 0.55; }
  .btn-primary:disabled:hover { background: var(--text-faint); }
  .btn-secondary { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-secondary:hover { background: var(--surface-raised); color: var(--text); }
  .btn-accent-ghost { background: var(--accent-dim); border: 1px solid rgba(26,79,168,0.22); color: var(--accent-mid); }
  .btn-accent-ghost:hover { background: var(--accent); color: #fff; border-color: var(--accent); }

  /* USER PILL */
  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 11px; background: var(--surface-raised); border: 1px solid var(--border); border-radius: 100px; font-size: var(--text-xs); color: var(--text-muted); font-weight: 500; }
  .online-dot { width: 6px; height: 6px; background: var(--green); border-radius: 50%; flex-shrink: 0; }

  /* SETTINGS DROPDOWN */
  .settings-wrap { position: relative; }
  .settings-menu { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 320px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: 0 4px 12px rgba(0,0,0,0.08), 0 12px 36px rgba(0,0,0,0.10); z-index: 200; padding: 8px; overflow: hidden; }
  .settings-menu.open { display: block; }
  .settings-row { white-space: nowrap; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; font-size: 14px; font-weight: 500; color: var(--text); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--transition); }
  .settings-row:hover { background: var(--surface-raised); }
  .settings-divider { height: 1px; background: var(--border); margin: 6px 4px; }
  .settings-section-label { font-family: var(--sans); font-size: 12px; font-weight: 600; letter-spacing: 0; color: var(--text-faint); padding: 8px 12px 6px; }
  /* Table density, Cal.com nav-pill-group: soft pill wrapper with the active
     segment as a canvas pill + soft shadow (readable on light and dark). */
  .density-btns { display: flex; gap: 4px; padding: 5px; margin: 0 8px 8px; background: var(--surface-sunken); border-radius: 9999px; }
  .density-btn { flex: 1; white-space: nowrap; padding: 7px 6px; font-size: 12px; font-weight: 500; background: transparent; border: none; border-radius: 9999px; color: var(--text-muted); cursor: pointer; font-family: var(--sans); text-align: center; transition: background var(--transition), color var(--transition), box-shadow var(--transition); }
  .density-btn.active { background: var(--surface); color: var(--text); box-shadow: 0 1px 2px rgba(0,0,0,0.08); }
  .density-btn:hover:not(.active) { color: var(--text); }

  /* TOGGLE */
  .toggle { position: relative; width: 30px; height: 16px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: var(--border-strong); border-radius: 9999px; cursor: pointer; transition: background var(--transition); }
  .toggle-track::after { content: ''; position: absolute; width: 12px; height: 12px; left: 2px; top: 2px; background: white; border-radius: 50%; transition: transform var(--transition); box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
  .toggle input:checked ~ .toggle-track { background: var(--accent-mid); }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* MAIN LAYOUT */
  .dash-main { max-width: 1580px; margin: 0 auto; padding: 24px 28px 40px; display: flex; flex-direction: column; gap: 20px; }
  .dashboard-greeting { padding: 2px 2px 4px; }
  .dashboard-greeting-text { font-size: var(--text-lg); font-weight: 600; color: var(--text); letter-spacing: -0.01em; }

  /* STATS GRID */
  .stats-grid { display: grid; grid-template-columns: 1.3fr 1fr 1fr 1fr; gap: 12px; }
  .stat-card { background: var(--surface-sunken); border: 1px solid transparent; border-radius: var(--radius-lg); padding: 22px; box-shadow: none; transition: background var(--transition), border-color var(--transition); position: relative; overflow: hidden; display: flex; flex-direction: column; gap: 10px; }
  .stat-card:hover { background: var(--surface-raised); border-color: var(--border); }
  .dash-root.dark .stat-card { background: var(--surface-raised); border-color: var(--border); }
    .stat-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
  .stat-label { font-family: var(--sans); font-size: 13px; font-weight: 500; letter-spacing: 0; color: var(--text-muted); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .stat-value-row { display: flex; align-items: baseline; gap: 10px; }
  .stat-value { font-family: "Cal Sans", var(--sans); font-size: var(--text-xl); font-weight: 600; letter-spacing: -0.5px; line-height: 1.1; color: var(--text); flex-shrink: 0; }
  .stat-value.c-accent { color: var(--accent-mid); }
  .stat-value.c-green { color: var(--green); }
  .stat-value.c-amber { color: var(--amber); }
  .stat-comparison { font-family: var(--sans); font-size: var(--text-2xs); color: var(--text-faint); letter-spacing: 0.02em; }
  .stat-comparison b { color: var(--text-muted); font-weight: 500; }

  /* TABLE TOOLBAR */
  .table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 9px 16px; border-bottom: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .toolbar-label { font-family: var(--sans); font-size: var(--text-xs); color: var(--text-faint); letter-spacing: 0.04em; }

  /* TABLE */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-sm); overflow: hidden; }
  .table-scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { border-bottom: 1px solid var(--border); background: var(--surface-sunken); }
  th { padding: 9px 14px; text-align: left; font-family: var(--sans); font-size: var(--text-sm); letter-spacing: 0; text-transform: none; color: var(--text-muted); font-weight: 500; }
  tbody tr { border-bottom: 1px solid var(--border); cursor: pointer; background-color: var(--surface); transition: background-color 0.12s ease; }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background-color: var(--row-hover); }
  tbody tr:hover td { background-color: var(--row-hover) !important; }
  /* Single-line rows: cells never wrap (the wrapper scrolls if the row is
     wider than the viewport), with a min width per column so text has room
     to breathe instead of being squeezed. overflow-wrap is reset because the
     dashboard root sets it to anywhere, which broke emails/names mid-word. */
  th, td { white-space: nowrap; overflow-wrap: normal; word-break: normal; }
  td { padding: 13px 14px; vertical-align: middle; font-size: var(--text-sm); }
  th:nth-child(1), td:nth-child(1) { min-width: 160px; }
  th:nth-child(2), td:nth-child(2) { min-width: 220px; }
  th:nth-child(3), td:nth-child(3) { min-width: 90px; }
  th:nth-child(4), td:nth-child(4) { min-width: 140px; }
  th:nth-child(5), td:nth-child(5) { min-width: 110px; }

  .density-compact td { padding: 8px 14px; }
  .density-comfortable td { padding: 13px 14px; }
  .density-spacious td { padding: 19px 14px; }

  /* Keep the outer edges a touch roomier, above the density padding. */
  th:first-child, td:first-child { padding-left: 18px; }
  th:last-child, td:last-child { padding-right: 18px; }

  /* TABLE CELLS */
  .cell-company-name { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.02em; line-height: 1.4; white-space: nowrap; }
  .cell-company-num { font-family: var(--sans); font-size: var(--text-xs); color: var(--accent-mid); white-space: nowrap; }
  .cell-sic { font-family: var(--sans); font-size: 10px; color: var(--text-faint); margin-top: 2px; }
  .date-val { font-family: var(--sans); font-size: 12px; color: var(--text); white-space: nowrap; }
  /* Role badges are inline pills here, drop the stacked-cell top margin so
     they sit centred with the rest of the row at every density. */
  td .badge-new { margin-top: 0; }

  /* BADGES */
  .badge-new { display: inline-flex; align-items: center; gap: 3px; padding: 1px 7px; background: var(--green-dim); border: 1px solid rgba(10,124,92,0.2); border-radius: 100px; font-size: 11px; font-weight: 600; letter-spacing: 0; color: var(--green); margin-top: 4px; }
  .badge-new::before { content: ''; width: 4px; height: 4px; background: var(--green); border-radius: 50%; }
  .badge-emailed { display: inline-flex; align-items: center; gap: 3px; padding: 1px 6px; background: var(--accent-dim); border: 1px solid rgba(26,79,168,0.15); border-radius: 100px; font-size: 11px; font-weight: 600; letter-spacing: 0; color: var(--accent-mid); margin-top: 3px; margin-right: 3px; }
  .badge-admin { background: var(--amber-dim); border: 1px solid rgba(154,93,0,0.22); color: var(--amber); }
  .badge-admin::before { background: var(--amber); }

  /* ROW ACTION BUTTONS */
  .row-quick-actions { display: flex; align-items: center; gap: 4px; opacity: 0; transition: opacity 0.12s ease; flex-shrink: 0; }
  tbody tr:hover .row-quick-actions { opacity: 1; }
  .row-action-btn { display: inline-flex; align-items: center; justify-content: center; width: 27px; height: 27px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text-faint); cursor: pointer; transition: background var(--transition), border-color var(--transition), color var(--transition); flex-shrink: 0; position: relative; }
  .row-action-btn:hover { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .row-action-btn .row-tooltip { position: absolute; bottom: calc(100% + 5px); left: 50%; transform: translateX(-50%) translateY(0); background: var(--text); color: var(--surface); font-size: 10px; font-family: var(--sans); white-space: nowrap; padding: 3px 6px; border-radius: 4px; opacity: 0; transition: opacity 0.12s ease, transform 0.12s ease; pointer-events: none; z-index: 10; }
  .row-action-btn:hover .row-tooltip { opacity: 1; transform: translateX(-50%) translateY(-3px); }

  /* FORM ELEMENTS */
  .field-group { display: flex; flex-direction: column; gap: 5px; }
  .field-label, .field-group label { font-family: var(--sans); font-size: 12px; font-weight: 500; letter-spacing: 0; color: var(--text-muted); }
  .field-input { width: 100%; padding: 8px 12px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: var(--text-sm); outline: none; transition: border-color var(--transition), box-shadow var(--transition); -webkit-appearance: none; }
  .field-input::placeholder { color: var(--text-faint); }
  .field-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 2px var(--accent-dim); }
  select.field-input { cursor: pointer; padding-right: 34px; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239da3b0' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; }

  .field-password-wrap { position: relative; }
  .field-password-wrap .field-input { padding-right: 40px; font-family: var(--sans); letter-spacing: 0.05em; }
  .field-eye-btn { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--text-faint); cursor: pointer; padding: 2px; display: flex; align-items: center; transition: color var(--transition); }
  .field-eye-btn:hover { color: var(--text-muted); }

  /* PASSWORD STRENGTH */
  .pw-strength { margin-top: 8px; }
  .pw-strength-bar { display: flex; gap: 3px; margin-bottom: 5px; }
  .pw-strength-seg { height: 3px; flex: 1; border-radius: 4px; background: var(--border); transition: background 0.3s ease; }
  .pw-strength-seg.filled-weak { background: var(--red); }
  .pw-strength-seg.filled-fair { background: var(--amber); }
  .pw-strength-seg.filled-good { background: #3b82f6; }
  .pw-strength-seg.filled-strong { background: var(--green); }
  .pw-strength-label { font-family: var(--sans); font-size: 10px; color: var(--text-faint); }
  .pw-strength-label.weak { color: var(--red); }
  .pw-strength-label.fair { color: var(--amber); }
  .pw-strength-label.good { color: #3b82f6; }
  .pw-strength-label.strong { color: var(--green); }

  .field-hint { font-family: var(--sans); font-size: 10.5px; margin-top: 5px; }
  .field-hint.match { color: var(--green); }
  .field-hint.no-match { color: var(--red); }

  /* SKELETON */
  .skeleton { background: linear-gradient(90deg, var(--surface-raised) 25%, var(--border) 50%, var(--surface-raised) 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 4px; }
  @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  .skeleton-row td { padding: 13px 16px; }
  .skeleton-text { height: 12px; border-radius: 4px; }
  .skeleton-text.w-80 { width: 80%; }
  .skeleton-text.w-60 { width: 60%; }
  .skeleton-text.w-40 { width: 40%; }
  .skeleton-text.w-30 { width: 30%; }

  /* EMPTY STATE */
  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 13px; text-align: center; }
  .empty-icon { font-size: 32px; opacity: 0.3; }
  .empty-title { font-size: var(--text-base); font-weight: 600; color: var(--text-muted); }
  .empty-sub { font-size: var(--text-sm); color: var(--text-faint); max-width: 320px; line-height: 1.6; }

  /* PAGINATION */
  .pagination { display: flex; align-items: center; justify-content: space-between; padding: 11px 16px; border-top: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .page-info { font-family: var(--sans); font-size: var(--text-xs); color: var(--text-faint); letter-spacing: 0.04em; }
  .page-info b { color: var(--text-muted); font-weight: 500; }
  .page-controls { display: flex; align-items: center; gap: 2px; flex-wrap: wrap; }
  .btn-page { display: flex; align-items: center; justify-content: center; min-width: 29px; height: 29px; padding: 0 8px; background: transparent; border: 1px solid var(--border); color: var(--text-muted); border-radius: var(--radius-sm); font-family: var(--sans); font-size: 11px; cursor: pointer; transition: background var(--transition), border-color var(--transition), color var(--transition); }
  .btn-page:hover:not(:disabled) { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .btn-page:disabled { opacity: 0.25; cursor: not-allowed; }
  .btn-page.active { background: var(--accent); border-color: var(--accent); color: #fff; font-weight: 600; }
  .btn-page.active:hover { background: var(--accent-hover); }
  .page-ellipsis { display: inline-flex; align-items: center; justify-content: center; width: 22px; color: var(--text-faint); font-size: 11px; }

  /* MODAL (matches CustomerDashboard cancel-modal) */
  .modal-backdrop { position: fixed; inset: 0; background: rgba(9,12,20,0.45); z-index: 400; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .modal-box { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 30px; max-width: 440px; width: 100%; box-shadow: var(--shadow-lg); max-height: 90vh; overflow-y: auto; }
  .modal-title { font-size: var(--text-md); font-weight: 600; color: var(--text); margin-bottom: 7px; letter-spacing: -0.02em; }
  .modal-subtitle { font-family: var(--sans); font-size: var(--text-xs); color: var(--text-faint); margin-bottom: 24px; letter-spacing: 0.04em; }
  .modal-divider { border: none; border-top: 1px solid var(--border); margin: 20px 0; }
  .modal-actions { display: flex; gap: 9px; justify-content: flex-end; margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border); }
  .modal-actions .btn { min-width: 100px; }

  /* TOAST NOTIFICATIONS */
  .toast-container { position: fixed; bottom: 24px; left: 24px !important; z-index: 9999 !important; display: flex; flex-direction: column; gap: 9px; pointer-events: none; }
  .toast { display: flex; align-items: flex-start; gap: 11px; padding: 13px 15px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-md); font-size: 13px; color: var(--text); max-width: 360px; pointer-events: all; animation: toastIn 0.22s cubic-bezier(0.22, 1, 0.36, 1); }
  .toast-icon { width: 28px; height: 28px; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
  .toast-icon svg { width: 15px; height: 15px; }
  .toast-success .toast-icon { background: var(--green-dim); color: var(--green); }
  .toast-error .toast-icon { background: var(--red-dim); color: var(--red); }
  .toast-info .toast-icon { background: var(--accent-dim); color: var(--accent); }
  .toast-content { flex: 1; min-width: 0; }
  .toast-headline { font-weight: 500; font-size: 13px; }
  .toast-sub { font-size: 12px; color: var(--text-muted); margin-top: 2px; line-height: 1.45; }
  .toast-dismiss { width: 22px; height: 22px; border-radius: 6px; border: none; background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--text-faint); padding: 0; flex-shrink: 0; }
  .toast-dismiss:hover { background: var(--surface-raised); color: var(--text-muted); }
  .toast-dismiss svg { width: 12px; height: 12px; }
  @keyframes toastIn { from { transform: translateX(16px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  /* RESPONSIVE */
  @media (max-width: 900px) {
    .dash-header-inner { padding: 0 16px; }
    .dash-main { padding: 16px 16px 32px; }
    .stats-grid { grid-template-columns: repeat(2, 1fr); }
    .stat-card:first-child { grid-column: span 2; }
  }
  @media (max-width: 480px) {
    .stats-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .stat-value { font-size: 22px; }
    .dash-brand-sub { display: none; }
  }
`;

// ── ICONS ─────────────────────────────────────────────────────────────────────
const Icon = {
  ChevronDown: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  Key: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Shield: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Trash: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
};

// ── TOAST SYSTEM (mirrors CustomerDashboard) ─────────────────────────────────
let toastIdCounter = 0;

const TOAST_ICONS = {
  success: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5.5 8l1.8 1.8 3.2-3.2" />
    </svg>
  ),
  error: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" />
    </svg>
  ),
  info: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 7.5v4M8 5.5v.01" />
    </svg>
  ),
};

const ToastContainer = ({ toasts, onDismiss }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast toast-${t.type}`}>
        <div className="toast-icon">
          {TOAST_ICONS[t.type] || TOAST_ICONS.info}
        </div>
        <div className="toast-content">
          <div className="toast-headline">{t.message}</div>
        </div>
        {onDismiss && (
          <button
            className="toast-dismiss"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss"
          >
            <svg
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M1 1l10 10M11 1L1 11" />
            </svg>
          </button>
        )}
      </div>
    ))}
  </div>
);

// ── PASSWORD SCORER ──────────────────────────────────────────────────────────
const scorePassword = (pw) => {
  if (!pw) return { score: 0, label: "", cls: "" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score: 1, label: "Weak", cls: "weak" };
  if (score === 2) return { score: 2, label: "Fair", cls: "fair" };
  if (score === 3) return { score: 3, label: "Good", cls: "good" };
  return { score: 4, label: "Strong", cls: "strong" };
};

const EyeIcon = ({ open }) =>
  open ? (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

// ── SKELETON ROWS ────────────────────────────────────────────────────────────
const SkeletonRows = ({ count = 4 }) => (
  <tr className="skeleton-row">
    <td colSpan={5} style={{ padding: "14px 12px" }}>
      <Skeleton lines={count} label="Loading team" />
    </td>
  </tr>
);

// ── CHANGE PASSWORD MODAL ────────────────────────────────────────────────────
const ChangePasswordModal = ({ member, onClose, onSuccess, onToast }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const passwordsMatch =
    confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordsMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = newPassword.length >= 8 && passwordsMatch && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      await apiService.adminChangeUserPassword(member.id, {
        password: newPassword,
      });
      onSuccess(`Password updated for ${member.name}`);
      onToast?.(`Password updated for ${member.name}`, "success");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update password");
      onToast?.("Failed to update password", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent title="Change Password" description={member.email}>
        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 16px",
              background: "var(--red-dim)",
              border: "1px solid rgba(239,68,68,0.22)",
              borderRadius: "var(--radius-sm)",
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--red)",
              marginBottom: 16,
            }}
          >
            ✕ {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="field-group" style={{ marginBottom: 16 }}>
            <PasswordStrength
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              rules={AXO_PASSWORD_RULES}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              required
            />
          </div>

          <div className="field-group" style={{ marginBottom: 16 }}>
            <PasswordField
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              autoComplete="new-password"
              required
            />
            {passwordsMatch && (
              <div className="field-hint match">✓ Passwords match</div>
            )}
            {passwordsMismatch && (
              <div className="field-hint no-match">
                ✕ Passwords do not match
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary btn-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="btn btn-primary btn-md"
            >
              {loading ? "Updating…" : "Update Password"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── CHANGE ROLE MODAL ────────────────────────────────────────────────────────
const ChangeRoleModal = ({ member, actor, onClose, onSuccess, onToast }) => {
  const options = assignableRoles(actor);
  const [role, setRole] = useState(member.role || "caller");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const definition = getRoleDefinition(role);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role || role === member.role) {
      onClose();
      return;
    }
    setError("");
    setLoading(true);
    try {
      await apiService.updateUserRole(member.id, { role });
      onSuccess?.();
      onToast?.(`${member.name} is now ${roleLabel(role)}`, "success");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update role");
      onToast?.("Failed to update role", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent title="Change role" description={member.email}>
        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 16px",
              background: "var(--red-dim)",
              border: "1px solid rgba(239,68,68,0.22)",
              borderRadius: "var(--radius-sm)",
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--red)",
              marginBottom: 16,
            }}
          >
            ✕ {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="field-group" style={{ marginBottom: 12 }}>
            <label className="field-label">Role for {member.name}</label>
            <Select
              label="Role"
              className="field-input"
              value={role}
              onValueChange={setRole}
              options={options.map((value) => ({
                value,
                label: roleLabel(value),
              }))}
            />
          </div>
          <div
            style={{
              padding: "10px 12px",
              background: "var(--surface-raised)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              fontSize: 12,
              color: "var(--text-muted)",
              lineHeight: 1.55,
              marginBottom: 16,
            }}
          >
            {definition.description}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary btn-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || role === member.role}
              className="btn btn-primary btn-md"
            >
              {loading ? "Saving…" : "Save Role"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── TEAM MANAGEMENT ──────────────────────────────────────────────────────────
const TeamManagement = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: "",
    name: "",
    role: "caller",
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [roleTarget, setRoleTarget] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  const [density, setDensity] = useState(
    () => localStorage.getItem("axo_density") || "comfortable",
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showAcct, setShowAcct] = useState(false);
  const [acctTab, setAcctTab] = useState("profile");
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const { toast } = useToastStack();
  const showToast = useCallback(
    (message, type = "info") => {
      toast({ title: message, type });
    },
    [toast],
  );

  const canManageTeam = hasCapability(user, CAPABILITIES.USERS_VIEW);
  const canManageRoles = hasCapability(user, CAPABILITIES.USERS_CHANGE_ROLE);
  const canRemoveUsers = hasCapability(user, CAPABILITIES.USERS_REMOVE);
  const canViewAudit = hasCapability(user, CAPABILITIES.AUDIT_VIEW);
  const canInviteUsers = hasCapability(user, CAPABILITIES.USERS_INVITE);
  const inviteRoleOptions = assignableRoles(user);

  useEffect(() => {
    loadUsers();
  }, []);
  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);
  useEffect(() => {
    localStorage.setItem("axo_density", density);
  }, [density]);
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const response = await apiService.getAuditLogs();
      setAuditLogs(response.data || []);
    } catch (err) {
      showToast("Failed to load activity log", "error");
    } finally {
      setAuditLoading(false);
    }
  };

  const openAuditLog = () => {
    setAuditOpen(true);
    loadAuditLogs();
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCustomerUsers();
      setUsers(response.data);
    } catch (err) {
      console.error("Failed to load users:", err);
      showToast("Failed to load team", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setError("");
    setInviteLoading(true);
    try {
      await apiService.inviteUser(inviteData);
      showToast("Invitation sent successfully!", "success");
      setShowInviteModal(false);
      setInviteData({ email: "", name: "", role: "caller" });
      loadUsers();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to send invitation";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!removeTarget) return;
    setRemoveLoading(true);
    try {
      await apiService.removeUser(removeTarget.id);
      showToast(`${removeTarget.name} removed`, "success");
      setRemoveTarget(null);
      loadUsers();
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to remove user",
        "error",
      );
    } finally {
      setRemoveLoading(false);
    }
  };

  const seatEntitlement = user?.customer?.entitlements?.seats || null;
  const seatsUnlimited =
    seatEntitlement?.isUnlimited === true || (seatEntitlement?.limit ?? 0) >= 999;
  const maxUsers = seatsUnlimited
    ? null
    : seatEntitlement?.limit ??
      user?.customer?.features?.max_users ??
      5;
  const maxUsersLabel = seatsUnlimited ? "Unlimited" : maxUsers;
  const canInvite = seatsUnlimited || users.length < maxUsers;

  const densityClass =
    {
      compact: "density-compact",
      comfortable: "density-comfortable",
      spacious: "density-spacious",
    }[density] || "density-comfortable";

  const getGreeting = () => {
    const hour = new Date().getHours();
    const timeOfDay =
      hour < 5
        ? "evening"
        : hour < 12
          ? "morning"
          : hour < 18
            ? "afternoon"
            : "evening";
    const firstName = user?.name?.trim()?.split(/\s+/)[0] || "there";
    return `Good ${timeOfDay}, ${firstName}`;
  };

  return (
    <>
      <style>
        {styles}
        {ACCOUNT_SETTINGS_STYLES}
      </style>
      <div className={`dash-root${darkMode ? " dark" : ""}`}>
        {/* HEADER */}
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <Link
                to="/dashboard"
                className="btn-back"
                title="Back to Dashboard"
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 12H5M12 5l-7 7 7 7"
                  />
                </svg>
              </Link>
              <div className="dash-brand-logo">
                <img src="/assets/logo.png" alt="logo" />
              </div>
              <div className="dash-brand-text">
                <div className="dash-brand-name">Team Management</div>
                <div className="dash-brand-sub">Customer Portal</div>
              </div>
            </div>

            <nav className="dash-nav">
              <Link to="/dashboard" className="btn btn-ghost btn-sm">
                Dashboard
              </Link>

              <div
                className="settings-wrap"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSettingsOpen((o) => !o)}
                >
                  Settings <Icon.ChevronDown />
                </button>
                <div className={`settings-menu${settingsOpen ? " open" : ""}`}>
                  <div className="settings-row">
                    <span>Dark Mode</span>
                    <ThemeSwitch
                      iconOnly
                      theme={darkMode ? "dark" : "light"}
                      onThemeChange={(next, variant, trigger) => applyThemeWithTransition(next, trigger, setDarkMode, variant)}
                    />
                  </div>
                  <div className="settings-divider" />
                  <div className="settings-section-label">Table Density</div>
                  <div className="density-btns">
                    {["compact", "comfortable", "spacious"].map((d) => (
                      <button
                        key={d}
                        className={`density-btn${density === d ? " active" : ""}`}
                        onClick={() => setDensity(d)}
                      >
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="settings-divider" />
                  <div
                    className="settings-row"
                    onClick={() => {
                      setAcctTab("profile");
                      setShowAcct(true);
                      setSettingsOpen(false);
                    }}
                  >
                    <span>Profile &amp; channels</span>
                    <span
                      style={{
                        display: "inline-flex",
                        transform: "rotate(-90deg)",
                      }}
                    >
                      <Icon.ChevronDown />
                    </span>
                  </div>
                  <div
                    className="settings-row"
                    onClick={() => {
                      setAcctTab("billing");
                      setShowAcct(true);
                      setSettingsOpen(false);
                    }}
                  >
                    <span>Plan &amp; billing</span>
                    <span
                      style={{
                        display: "inline-flex",
                        transform: "rotate(-90deg)",
                      }}
                    >
                      <Icon.ChevronDown />
                    </span>
                  </div>
                </div>
              </div>

              <div className="user-pill">
                <div className="online-dot" />
                {user?.name}
              </div>
              <button onClick={logout} className="btn btn-ghost btn-sm">
                Logout
              </button>
            </nav>
          </div>
        </header>

        {/* MAIN */}
        <div className="dash-main">
          {/* GREETING */}
          <div className="dashboard-greeting">
            <div className="dashboard-greeting-text">{getGreeting()}</div>
          </div>

          {/* STATS */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card-top">
                <div className="stat-label">Team Members</div>
              </div>
              <div className="stat-value-row">
                <div className="stat-value c-accent">
                  {users.length.toLocaleString()}
                </div>
                <div className="stat-comparison">
                  of <b>{maxUsersLabel}</b> seats
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-top">
                <div className="stat-label">Owners &amp; admins</div>
              </div>
              <div className="stat-value-row">
                <div className="stat-value c-amber">
                  {users.filter((u) => ["owner", "admin"].includes(u.role)).length}
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-top">
                <div className="stat-label">Managers</div>
              </div>
              <div className="stat-value-row">
                <div className="stat-value c-green">
                  {users.filter((u) => u.role === "manager").length}
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-top">
                <div className="stat-label">Seats Available</div>
              </div>
              <div className="stat-value-row">
                <div className="stat-value">
                  {seatsUnlimited ? "∞" : Math.max(0, maxUsers - users.length)}
                </div>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="table-wrap">
            {!loading && users.length > 0 && (
              <div className="table-toolbar">
                <span className="toolbar-label">
                  {users.length.toLocaleString()} members
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="toolbar-label">
                    {users.length} / {maxUsersLabel} seats used
                  </span>
                  {canViewAudit && (
                    <button
                      onClick={openAuditLog}
                      className="btn btn-secondary btn-sm"
                    >
                      Activity log
                    </button>
                  )}
                  {canInviteUsers && (
                    <button
                      onClick={() => setShowInviteModal(true)}
                      disabled={!canInvite}
                      className="btn btn-primary btn-sm"
                    >
                      + Invite User
                    </button>
                  )}
                </div>
              </div>
            )}

            {loading ? (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Invited By</th>
                      <th>Joined</th>
                      {canManageTeam && <th style={{ width: 120 }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    <SkeletonRows count={4} />
                  </tbody>
                </table>
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><Users size={32} strokeWidth={1.5} /></div>
                <div className="empty-title">No team members yet</div>
                <div className="empty-sub">
                  Invite your first user to get started
                </div>
                {canInviteUsers && (
                  <button
                    className="btn btn-primary btn-md"
                    onClick={() => setShowInviteModal(true)}
                    style={{ marginTop: 8 }}
                  >
                    + Invite User
                  </button>
                )}
              </div>
            ) : (
              <SortableDataTable
                caption="Team members"
                rowKey="id"
                defaultSort={{ key: "name", direction: "asc" }}
                rows={users}
                columns={[
                  {
                    key: "name",
                    label: "Name",
                    render: (value) => (
                      <div className="cell-company-name">{value}</div>
                    ),
                  },
                  {
                    key: "email",
                    label: "Email",
                    render: (value) => (
                      <div className="cell-company-num">{value}</div>
                    ),
                  },
                  {
                    key: "role",
                    label: "Role",
                    render: (value) => (
                      <span
                        className={`badge-new ${["admin", "owner"].includes(normaliseRole(value)) ? "badge-admin" : ""}`}
                      >
                        {roleLabel(value)}
                      </span>
                    ),
                  },
                  {
                    key: "_inviter",
                    label: "Invited By",
                    sortable: false,
                    render: (_value, row) => (
                      <div className="date-val">
                        {row.inviter?.id === row.id
                          ? "Self-registered"
                          : row.inviter?.name || "-"}
                      </div>
                    ),
                  },
                  {
                    key: "createdAt",
                    label: "Joined",
                    render: (value) => (
                      <div className="date-val">
                        {new Date(value).toLocaleDateString("en-GB")}
                      </div>
                    ),
                  },
                  ...(canManageTeam
                    ? [
                        {
                          key: "_actions",
                          label: "",
                          sortable: false,
                          render: (_value, row) => (
                            <div
                              className="row-quick-actions"
                              style={{ justifyContent: "flex-end" }}
                            >
                              {canManageRoles &&
                                canManageUser(user, row) &&
                                normaliseRole(row.role) !== "owner" && (
                                  <button
                                    className="row-action-btn"
                                    onClick={() => setRoleTarget(row)}
                                    title={`Change role for ${row.name}`}
                                  >
                                    <span className="row-tooltip">
                                      Change role
                                    </span>
                                    <Icon.Shield />
                                  </button>
                                )}
                              <button
                                className="row-action-btn"
                                onClick={() => setPasswordTarget(row)}
                                title={`Change password for ${row.name}`}
                              >
                                <span className="row-tooltip">
                                  Change Password
                                </span>
                                <Icon.Key />
                              </button>
                              {canRemoveUsers &&
                                row.id !== user?.id &&
                                normaliseRole(row.role) !== "owner" && (
                                  <button
                                    className="row-action-btn"
                                    onClick={() => setRemoveTarget(row)}
                                    title={`Remove ${row.name}`}
                                    style={{ color: "var(--red)" }}
                                  >
                                    <span className="row-tooltip">
                                      Remove user
                                    </span>
                                    <Icon.Trash />
                                  </button>
                                )}
                            </div>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            )}
          </div>
        </div>

        {/* INVITE MODAL */}
        {showInviteModal && (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next) {
                setShowInviteModal(false);
                setError("");
              }
            }}
          >
            <DialogContent
              title="Invite Team Member"
              description={`${users.length} / ${maxUsersLabel} seats used`}
            >
              {error && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "12px 16px",
                    background: "var(--red-dim)",
                    border: "1px solid rgba(239,68,68,0.22)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--red)",
                    marginBottom: 16,
                  }}
                >
                  ✕ {error}
                </div>
              )}

              <form onSubmit={handleInvite}>
                <div className="field-group" style={{ marginBottom: 16 }}>
                  <label className="field-label">Full Name</label>
                  <Input
                    label="Full Name"
                    hideLabel
                    type="text"
                    value={inviteData.name}
                    onChange={(e) =>
                      setInviteData({ ...inviteData, name: e.target.value })
                    }
                    required
                    className="field-input"
                    placeholder="Jane Smith"
                  />
                </div>
                <div className="field-group" style={{ marginBottom: 16 }}>
                  <label className="field-label">Email</label>
                  <Input
                    label="Email"
                    hideLabel
                    type="email"
                    value={inviteData.email}
                    onChange={(e) =>
                      setInviteData({ ...inviteData, email: e.target.value })
                    }
                    required
                    className="field-input"
                    placeholder="jane@company.com"
                  />
                </div>
                <div className="field-group" style={{ marginBottom: 16 }}>
                  <Select
                    label="Role"
                    className="field-input"
                    value={inviteData.role}
                    onValueChange={(value) =>
                      setInviteData({ ...inviteData, role: value })
                    }
                    options={inviteRoleOptions.map((value) => ({
                      value,
                      label: roleLabel(value),
                    }))}
                  />
                  <div
                    style={{
                      fontSize: 11.5,
                      color: "var(--text-faint)",
                      lineHeight: 1.5,
                      marginTop: 6,
                    }}
                  >
                    {getRoleDefinition(inviteData.role).description}
                  </div>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setError("");
                    }}
                    className="btn btn-secondary btn-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="btn btn-primary btn-md"
                  >
                    {inviteLoading ? "Sending…" : "Send Invitation"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* CHANGE PASSWORD MODAL */}
        {passwordTarget && (
          <ChangePasswordModal
            member={passwordTarget}
            onClose={() => setPasswordTarget(null)}
            onSuccess={() => loadUsers()}
            onToast={showToast}
          />
        )}

        {/* ACCOUNT SETTINGS MODAL, same component/settings as the dashboard */}
        {showAcct && (
          <AccountSettingsModal
            user={user}
            initialTab={acctTab}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            density={density}
            setDensity={setDensity}
            onClose={() => setShowAcct(false)}
            onRequestCancel={() =>
              showToast("Manage your subscription from the dashboard", "info")
            }
          />
        )}

        {/* CHANGE ROLE MODAL */}
        {roleTarget && (
          <ChangeRoleModal
            member={roleTarget}
            actor={user}
            onClose={() => setRoleTarget(null)}
            onSuccess={loadUsers}
            onToast={showToast}
          />
        )}

        {/* ACTIVITY LOG MODAL */}
        {auditOpen && (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next) setAuditOpen(false);
            }}
          >
            <DialogContent
              title="Activity log"
              description="Sensitive team actions taken by owners and admins."
            >
              {auditLoading ? (
                <div className="empty-sub">Loading activity…</div>
              ) : auditLogs.length === 0 ? (
                <div className="empty-sub">No admin actions recorded yet.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {auditLogs.map((log) => (
                    <div
                      key={log.id}
                      style={{
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius-sm)",
                        padding: "10px 12px",
                        background: "var(--surface-raised)",
                      }}
                    >
                      <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                        {log.action === "user.removed" && "User removed"}
                        {log.action === "user.role_changed" && "Role changed"}
                        {log.action === "user.password_reset" && "Password reset"}
                        {log.action === "user.invited" && "User invited"}
                        {log.action === "user.retired" && "Account retired"}
                        {log.action === "customer.created" && "Organisation created"}
                        {![
                          "user.removed",
                          "user.role_changed",
                          "user.password_reset",
                          "user.invited",
                          "user.retired",
                          "customer.created",
                        ].includes(log.action) && log.action}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                        {log.targetName || log.targetEmail || "-"}
                        {log.metadata?.from && log.metadata?.to
                          ? ` · ${log.metadata.from} → ${log.metadata.to}`
                          : ""}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 3 }}>
                        by {log.actorName || log.actorEmail || "System"}
                        {log.actorRole ? ` (${roleLabel(log.actorRole)})` : ""}
                        {" · "}
                        {new Date(log.createdAt).toLocaleString("en-GB")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* REMOVE USER CONFIRM MODAL */}
        {removeTarget && (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next && !removeLoading) setRemoveTarget(null);
            }}
          >
            <DialogContent
              title="Remove team member"
              description={removeTarget.email}
            >
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  lineHeight: 1.6,
                }}
              >
                Are you sure you want to remove{" "}
                <strong>{removeTarget.name}</strong>? They'll immediately lose
                access to this account. This can't be undone.
              </p>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setRemoveTarget(null)}
                  disabled={removeLoading}
                  className="btn btn-secondary btn-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRemoveUser}
                  disabled={removeLoading}
                  className="btn btn-primary btn-md"
                  style={{
                    background: "var(--red)",
                    borderColor: "var(--red)",
                  }}
                >
                  {removeLoading ? "Removing…" : "Remove User"}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </>
  );
};

export default TeamManagement;
